import { baseApi } from './base.api';
import { API_VERSION, PATH_ANALYTICS_API } from 'src/constants/api-routes';
import moment from 'moment/moment';
import { formatDuration } from 'src/utils/formatDuration';

interface CoCustomerStatistics {
  total_resolved_chats?: number;
  total_escalated_chats?: number;
  total_in_progress?: number;
  total_ai_responses?: number;
  ai_replies_per_processed_chat_avg?: number;
  first_response_time_avg?: number;
  response_time_avg?: number;
  resolution_time_avg?: number;
  total_processed_chats?: number;
  resolution_rate?: number;
  total_escalated_other_chats?: number;
  total_escalated_user_request_chats?: number;
  total_escalated_knowledge_gap_chats?: number;
  total_escalated_model_configuration_chats?: number;
}

type ProjectIdsParam = (string | undefined)[] | string;

export interface CoCustomerTimeSeriesArgs {
  project_ids?: ProjectIdsParam;
  start_time?: string;
  end_time?: string;
  group_by?: string;
  tz_offset?: number | string;
}

export interface CoCustomerTimeSeriesRawPoint extends CoCustomerStatistics {
  grouped_by: string;
}

export interface CoCustomerTimeSeriesPoint extends CoCustomerTimeSeriesRawPoint {
  resolutionRate: number;
  totalProcessedTickets: number;
  resolvedTickets: number;
  escalatedTickets: number;
  escalatedOther: number;
  escalatedUserRequest: number;
  escalatedKnowledgeGap: number;
  escalatedModelConfiguration: number;
  inProgressTickets: number;
  aiRepliesSent: number;
  aiRepliesPerProcessedTicket: number;
  firstResponseTimeAvg_sec: number;
  firstResponseTimeAvg_min: number;
  firstResponseTimeAvg_hr: number;
  responseTimeAvg_sec: number;
  responseTimeAvg_min: number;
  responseTimeAvg_hr: number;
  resolutionTimeAvg_sec: number;
  resolutionTimeAvg_min: number;
  resolutionTimeAvg_hr: number;
  formattedGroup: string;
}

export interface CoCustomerAggregatedArgs {
  changes_percentage?: boolean | string;
  start_time?: string;
  end_time?: string;
  project_ids?: ProjectIdsParam;
}

export interface CoCustomerAggregatedResponse {
  statistics: CoCustomerStatistics;
  changes_percentages?: CoCustomerStatistics;
}

export interface AnalyticsMetric<T = number> {
  value: T;
  // Missing when the API has no previous period to compare against.
  percentage: number;
}

export interface CoCustomerAggregated {
  response: CoCustomerAggregatedResponse;
  resolutionRate: AnalyticsMetric;
  totalProcessedTickets: AnalyticsMetric;
  resolvedTickets: AnalyticsMetric;
  escalatedTickets: AnalyticsMetric;
  escalatedOther: AnalyticsMetric;
  escalatedUserRequest: AnalyticsMetric;
  escalatedKnowledgeGap: AnalyticsMetric;
  escalatedModelConfiguration: AnalyticsMetric;
  inProgressTickets: AnalyticsMetric;
  aiRepliesSent: AnalyticsMetric;
  aiRepliesPerProcessedTicket: AnalyticsMetric;
  firstResponseTimeAvg: AnalyticsMetric<string | undefined>;
  responseTimeAvg: AnalyticsMetric<string | undefined>;
  resolutionTimeAvg: AnalyticsMetric<string | undefined>;
}

export interface CoCustomerChatsCountArgs {
  project_ids?: string;
  statuses?: string;
}

export interface CoCustomerChatsCount {
  count: number;
}

const getGroupLabel = (
  groupedBy: string,
  groupBy: string,
  isMoreThanDay: boolean,
  years: number,
) => {
  if (groupBy === 'hour') {
    return moment(groupedBy).format(isMoreThanDay ? 'MMM DD, hh:mm A' : 'hh:mm A');
  }

  return years > 1 ? moment(groupedBy).format('ll') : moment(groupedBy).format('MMM DD');
};

const toTimeSeriesPoints = (
  response: CoCustomerTimeSeriesRawPoint[],
  arg: CoCustomerTimeSeriesArgs,
): CoCustomerTimeSeriesPoint[] => {
  const years = new Set(response.map((item) => moment(item.grouped_by).year()));
  const isMoreThanDay = Boolean(moment(arg.end_time).diff(moment(arg.start_time), 'days'));

  return response
    .map((item) => ({
      ...item,
      resolutionRate: item.resolution_rate || 0,
      totalProcessedTickets: item.total_processed_chats,
      resolvedTickets: item.total_resolved_chats || 0,
      escalatedTickets: item.total_escalated_chats || 0,
      escalatedOther: item.total_escalated_other_chats || 0,
      escalatedUserRequest: item.total_escalated_user_request_chats || 0,
      escalatedKnowledgeGap: item.total_escalated_knowledge_gap_chats || 0,
      escalatedModelConfiguration: item.total_escalated_model_configuration_chats || 0,
      inProgressTickets: item.total_in_progress,
      aiRepliesSent: item.total_ai_responses,
      aiRepliesPerProcessedTicket: item.ai_replies_per_processed_chat_avg || 0,
      firstResponseTimeAvg_sec: item.first_response_time_avg || 0,
      firstResponseTimeAvg_min: item.first_response_time_avg
        ? item.first_response_time_avg / 60
        : 0,
      firstResponseTimeAvg_hr: item.first_response_time_avg
        ? item.first_response_time_avg / 3600
        : 0,
      responseTimeAvg_sec: item.response_time_avg || 0,
      responseTimeAvg_min: item.response_time_avg ? item.response_time_avg / 60 : 0,
      responseTimeAvg_hr: item.response_time_avg ? item.response_time_avg / 3600 : 0,
      resolutionTimeAvg_sec: item.resolution_time_avg || 0,
      resolutionTimeAvg_min: item.resolution_time_avg ? item.resolution_time_avg / 60 : 0,
      resolutionTimeAvg_hr: item.resolution_time_avg ? item.resolution_time_avg / 3600 : 0,
      formattedGroup: getGroupLabel(item.grouped_by, arg.group_by, isMoreThanDay, years.size),
    }))
    .sort((a, b) => moment(a.grouped_by).diff(moment(b.grouped_by)));
};

const toAggregatedMetrics = (response: CoCustomerAggregatedResponse): CoCustomerAggregated => {
  const statistics = response.statistics;
  const percentages = response.changes_percentages;

  return {
    response,
    resolutionRate: {
      value: statistics.resolution_rate || 0,
      percentage: percentages?.resolution_rate || 0,
    },
    totalProcessedTickets: {
      value: statistics.total_processed_chats,
      percentage: percentages?.total_processed_chats,
    },
    resolvedTickets: {
      value: statistics.total_resolved_chats || 0,
      percentage: percentages?.total_resolved_chats,
    },
    escalatedTickets: {
      value: statistics.total_escalated_chats || 0,
      percentage: percentages?.total_escalated_chats,
    },
    escalatedOther: {
      value: statistics.total_escalated_other_chats || 0,
      percentage: percentages?.total_escalated_other_chats || 0,
    },
    escalatedUserRequest: {
      value: statistics.total_escalated_user_request_chats || 0,
      percentage: percentages?.total_escalated_user_request_chats || 0,
    },
    escalatedKnowledgeGap: {
      value: statistics.total_escalated_knowledge_gap_chats || 0,
      percentage: percentages?.total_escalated_knowledge_gap_chats || 0,
    },
    escalatedModelConfiguration: {
      value: statistics.total_escalated_model_configuration_chats || 0,
      percentage: percentages?.total_escalated_model_configuration_chats || 0,
    },
    inProgressTickets: {
      value: statistics.total_in_progress,
      percentage: percentages?.total_in_progress,
    },
    aiRepliesSent: {
      value: statistics.total_ai_responses,
      percentage: percentages?.total_ai_responses,
    },
    aiRepliesPerProcessedTicket: {
      value: statistics.ai_replies_per_processed_chat_avg || 0,
      percentage: percentages?.ai_replies_per_processed_chat_avg || 0,
    },
    firstResponseTimeAvg: {
      value: formatDuration(statistics.first_response_time_avg),
      percentage: percentages?.first_response_time_avg,
    },
    responseTimeAvg: {
      value: formatDuration(statistics.response_time_avg),
      percentage: percentages?.response_time_avg,
    },
    resolutionTimeAvg: {
      value: formatDuration(statistics.resolution_time_avg),
      percentage: percentages?.resolution_time_avg,
    },
  };
};

export const coCustomerAnalyticsService = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getCoCustomerTimeSeries: build.query<CoCustomerTimeSeriesPoint[], CoCustomerTimeSeriesArgs>({
      query: ({
        project_ids = '',
        start_time = '',
        end_time = '',
        group_by = '',
        tz_offset = '',
      }) => ({
        url: `${API_VERSION.V1}/${PATH_ANALYTICS_API.COCUSTOMER.TIME_SERIES}`,
        params: {
          ...(project_ids !== '' && { project_ids }),
          ...(start_time !== '' && { start_time }),
          ...(end_time !== '' && { end_time }),
          ...(group_by !== '' && { group_by }),
          ...(tz_offset !== '' && { tz_offset }),
        },
      }),
      transformResponse: (response: CoCustomerTimeSeriesRawPoint[], _meta, arg) =>
        toTimeSeriesPoints(response, arg),
      providesTags: ['CoCustomerAnalyticsTimeSeries'],
    }),
    getCoCustomerAggregated: build.query<CoCustomerAggregated, CoCustomerAggregatedArgs>({
      query: ({ changes_percentage = '', start_time = '', end_time = '', project_ids = '' }) => ({
        url: `${API_VERSION.V1}/${PATH_ANALYTICS_API.COCUSTOMER.AGGREGATED}`,
        params: {
          ...(changes_percentage !== '' && { changes_percentage }),
          ...(start_time !== '' && { start_time }),
          ...(end_time !== '' && { end_time }),
          ...(project_ids !== '' && { project_ids }),
        },
      }),
      extraOptions: { maxRetries: 0 },
      transformResponse: (response: CoCustomerAggregatedResponse) => toAggregatedMetrics(response),
      keepUnusedDataFor: 0,
      providesTags: ['CoCustomerAnalyticsAggregated'],
    }),
    getCoCustomerChatsCount: build.query<CoCustomerChatsCount, CoCustomerChatsCountArgs>({
      query: ({ project_ids = '', statuses = '' }) => ({
        url: `${API_VERSION.V1}/${PATH_ANALYTICS_API.COCUSTOMER.CHATS_COUNT}`,
        params: {
          ...(project_ids !== '' && { project_ids }),
          ...(statuses !== '' && { statuses }),
        },
      }),
      providesTags: ['CoCustomerAnalyticsChatsCount'],
    }),
  }),
});

export const {
  useLazyGetCoCustomerTimeSeriesQuery,
  useLazyGetCoCustomerAggregatedQuery,
  useGetCoCustomerChatsCountQuery,
} = coCustomerAnalyticsService;
